import JSZip from "jszip";
import type { BundleIndex, BundleManifest, RunSummary } from "./types";
import { buildSearchIndex, clearSearchIndex } from "./search";

// Loads + parses a kilochain bundle zip from a File handle (drop-zone or
// file picker). Returns { manifest, index } or throws.
export async function loadBundle(file: File): Promise<{
  manifest: BundleManifest;
  index: BundleIndex;
}> {
  const buf = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buf);

  const manifestEntry = zip.file("manifest.json");
  if (!manifestEntry) throw new Error("bundle is missing manifest.json — is this a kilochain bundle?");
  const indexEntry = zip.file("index.json");
  if (!indexEntry) throw new Error("bundle is missing index.json — is this a kilochain bundle?");

  const [manifestText, indexText] = await Promise.all([
    manifestEntry.async("string"),
    indexEntry.async("string"),
  ]);

  const manifest = JSON.parse(manifestText) as BundleManifest;
  const index = JSON.parse(indexText) as BundleIndex;

  // Older bundles predate feed records — normalize to [] so the UI treats
  // "feeds field absent" the same as "no feeds this day".
  if (!Array.isArray(index.feeds)) index.feeds = [];

  // Compat shim for old (v1) bundles — backfill ddRunUrl from log events
  // and rewrite ddLogsUrl to use wildcard + time-window params. New (v2+)
  // bundles already do all of this server-side.
  for (const run of index.runs) {
    if (!run.ddRunUrl) {
      const derived = deriveDdRunUrl(run);
      run.ddRunUrl = derived;
      run.ddRunUrlIsRunSpecific = !!derived;
    } else if (run.ddRunUrlIsRunSpecific === undefined) {
      // v1 bundles always populated this from ddSourceUrl (or nothing), so
      // a non-null value is always run-specific in old bundles.
      run.ddRunUrlIsRunSpecific = true;
    }
    if (!run.ddLogsUrl.includes("from_ts=")) {
      run.ddLogsUrl = rebuildDdLogsUrl(run);
    }
  }

  // Stable ordinal numbering — earliest run = #1, independent of sort.
  assignOrdinals(index.runs);

  // Reset the module-level haystack cache so haystacks from a previously
  // loaded bundle don't linger in memory across loads.
  clearSearchIndex();
  // Pre-compute the search haystack for every run so per-keystroke
  // filtering stays cheap.
  for (const run of index.runs) buildSearchIndex(run);

  return { manifest, index };
}

function deriveDdRunUrl(run: RunSummary): string | null {
  const events = [...run.classifications, ...run.extractions, ...run.githubLookups];
  for (const ev of events) {
    const url = ev.data?.ddSourceUrl;
    if (typeof url === "string" && url.startsWith("http")) return url;
  }
  return null;
}

function rebuildDdLogsUrl(run: RunSummary): string {
  const startedMs = run.startedAt ? new Date(run.startedAt).getTime() : Date.now();
  const fromTs = startedMs - 60 * 60 * 1000;
  const toTs = startedMs + 23 * 60 * 60 * 1000;
  const q = encodeURIComponent(`service:kilochain ${run.shortRunId}*`);
  return (
    `https://app.datadoghq.com/logs?query=${q}` +
    `&agg_m=count&agg_m_source=base&agg_t=count` +
    `&cols=host%2Cservice&messageDisplay=inline&refresh_mode=sliding` +
    `&storage=flex_tier&stream_sort=desc&viz=stream` +
    `&from_ts=${fromTs}&to_ts=${toTs}&live=true`
  );
}

// Sort runs by startedAt in the requested direction.
//   "desc" = newest first (default; matches "what just happened?" reading)
//   "asc"  = oldest first (matches the day's chronological flow)
export type SortDir = "asc" | "desc";
export function sortedRuns(runs: RunSummary[], dir: SortDir = "desc"): RunSummary[] {
  return [...runs].sort((a, b) => {
    const aT = a.startedAt ?? "";
    const bT = b.startedAt ?? "";
    const cmp = aT.localeCompare(bT);
    return dir === "asc" ? cmp : -cmp;
  });
}

// Stamp each run with a 1-based ordinal in chronological order. Earliest
// run of the day = #1. Caller does this ONCE on bundle load so the ordinal
// is stable regardless of the user's chosen sort direction.
export function assignOrdinals(runs: RunSummary[]): void {
  const byTime = [...runs].sort((a, b) => {
    const aT = a.startedAt ?? "";
    const bT = b.startedAt ?? "";
    return aT.localeCompare(bT);
  });
  byTime.forEach((r, i) => {
    r.ordinal = i + 1;
  });
}

// Truthy if the run is real (actually processed packages or workflow events).
// Used to optionally hide manual-invocation noise like the `iam-verify` run
// that has 0 packages + 0 events.
export function isMeaningfulRun(run: RunSummary): boolean {
  return (
    run.totalPackages > 0 ||
    run.classifications.length > 0 ||
    run.extractions.length > 0 ||
    run.githubLookups.length > 0 ||
    run.unblocks.length > 0
  );
}
